/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { useCallback, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type SaSubscription } from '../../lib/api/sa.js';
import { useMarkSubscriptionPaid, useSaSubscriptions } from '../../lib/queries/sa.js';
import { formatInr } from '../../lib/utils/money.js';

const STATUS_TONE: Readonly<Record<string, BadgeTone>> = {
  active: 'green',
  trialing: 'violet',
  past_due: 'amber',
  suspended: 'rose',
  cancelled: 'slate',
  none: 'slate',
};

const COLS = ['clinic', 'plan', 'status', 'mrr', 'renews', 'actions'] as const;

function renewLabel(sub: SaSubscription): string {
  const date = sub.status === 'trialing' ? sub.trialEndsAt : sub.currentPeriodEnd;
  return date !== null ? date.slice(0, 10) : '—';
}

function SubscriptionRow({ sub }: { readonly sub: SaSubscription }): JSX.Element {
  const { t } = useTranslation();
  const markPaid = useMarkSubscriptionPaid();
  // Only clinics with a subscription that isn't already active can be marked paid.
  const canMark = sub.status !== 'active' && sub.status !== 'none';
  const onMarkPaid = useCallback(() => {
    markPaid.mutate(sub.clinicId);
  }, [markPaid, sub.clinicId]);
  return (
    <tr className="border-b border-slate-100 last:border-0 dark:border-slate-800">
      <td className="py-3 pr-4 font-medium text-slate-800 dark:text-slate-100">{sub.clinicName}</td>
      <td className="py-3 pr-4 text-slate-600 dark:text-slate-300">{sub.planName ?? '—'}</td>
      <td className="py-3 pr-4">
        <Badge
          label={t(`sa.subscriptions.status.${sub.status}`, sub.status)}
          tone={STATUS_TONE[sub.status] ?? 'slate'}
        />
      </td>
      <td className="py-3 pr-4 text-slate-700 dark:text-slate-200">{formatInr(sub.mrrPaise)}</td>
      <td className="py-3 pr-4 text-slate-500 dark:text-slate-400">{renewLabel(sub)}</td>
      <td className="py-3">
        {canMark && (
          <Button
            type="button"
            variant="outline"
            onClick={onMarkPaid}
            disabled={markPaid.isPending}
          >
            {t('sa.subscriptions.markPaid')}
          </Button>
        )}
      </td>
    </tr>
  );
}

/** Super Admin → Subscriptions. Every clinic's commercial state (read-only). */
export function SaSubscriptionsPage(): JSX.Element {
  const { t } = useTranslation();
  const query = useSaSubscriptions();
  const rows = query.data ?? [];

  return (
    <div className="p-8">
      <PageHeader
        title={t('sa.subscriptions.title')}
        subtitle={t('sa.subscriptions.count', { count: rows.length })}
      />
      <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-900">
        <QueryState
          isLoading={query.isLoading}
          isError={query.isError}
          isEmpty={rows.length === 0}
          loadingLabel={t('common.loading')}
          errorLabel={t('common.loadError')}
          emptyLabel={t('sa.subscriptions.empty')}
        >
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-400 dark:border-slate-700">
                {COLS.map((col) => (
                  <th key={col} className="pb-2 font-medium">
                    {t(`sa.subscriptions.col.${col}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((sub) => (
                <SubscriptionRow key={sub.clinicId} sub={sub} />
              ))}
            </tbody>
          </table>
        </QueryState>
      </div>
    </div>
  );
}
